/**
 * Slot Context for the persistent customer top bar (#2021).
 *
 * The wrapper owns the row (so its position never moves between pages),
 * and pages contribute their own left/right content through this Context.
 * The middle is non-negotiable — it's always the global search.
 *
 * Two contexts on purpose:
 *   - `TopBarSlotsValueContext`: `{ left, right }`. Only read by <TopBar>.
 *   - `TopBarSlotsSettersContext`: `{ setLeft, setRight }`. Read by pages.
 *
 * Splitting them prevents an infinite render loop: pages call `setLeft`
 * from an effect on every render, which changes the value context. If
 * pages also subscribed to the *value* context they'd re-render on every
 * update and set state again, ad infinitum. Setters are stable across
 * renders (React's `useState` guarantees it) so the setters context value
 * never changes — pages don't re-render when a slot changes.
 *
 * Usage from a page:
 *   useTopBarSlots({ left: <h1>Workspace</h1>, right: <UploadButton /> });
 */

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

interface TopBarSlots {
  left: ReactNode;
  right: ReactNode;
}

interface TopBarSlotsSetters {
  setLeft: (node: ReactNode) => void;
  setRight: (node: ReactNode) => void;
}

const TopBarSlotsValueContext = createContext<TopBarSlots | null>(null);
const TopBarSlotsSettersContext = createContext<TopBarSlotsSetters | null>(
  null
);

export function TopBarSlotsProvider({ children }: { children: ReactNode }) {
  const [left, setLeft] = useState<ReactNode>(null);
  const [right, setRight] = useState<ReactNode>(null);

  const value = useMemo<TopBarSlots>(() => ({ left, right }), [left, right]);

  // setState functions are stable across renders — memoize once with an
  // empty deps array so the setters context value never changes reference.
  const setters = useMemo<TopBarSlotsSetters>(
    () => ({ setLeft, setRight }),
    []
  );

  return (
    <TopBarSlotsSettersContext.Provider value={setters}>
      <TopBarSlotsValueContext.Provider value={value}>
        {children}
      </TopBarSlotsValueContext.Provider>
    </TopBarSlotsSettersContext.Provider>
  );
}

/** Read-only access for the TopBar component itself. */
export function useTopBarContent(): TopBarSlots {
  const ctx = useContext(TopBarSlotsValueContext);
  // Outside a provider (e.g. payment / share pages) → empty slots.
  if (!ctx) return { left: null, right: null };
  return ctx;
}

interface UseTopBarSlotsArgs {
  left?: ReactNode;
  right?: ReactNode;
}

/**
 * Page-side hook: declares what should appear in the top bar's left and/or
 * right slots while the page is mounted. Clears its slots on unmount so the
 * next page starts clean.
 *
 * Subscribes only to the setters context (stable reference), so this hook
 * never triggers a re-render on the caller — that's what prevents the
 * infinite loop when callers pass inline JSX (fresh reference each render).
 */
export function useTopBarSlots({ left, right }: UseTopBarSlotsArgs): void {
  const setters = useContext(TopBarSlotsSettersContext);

  // Push current slot contents after every render. No cleanup here — that
  // would null the slot between renders and cause flicker. Cleanup happens
  // in the dedicated unmount effect below.
  useEffect(() => {
    if (!setters) return;
    setters.setLeft(left ?? null);
    setters.setRight(right ?? null);
  });

  useEffect(() => {
    return () => {
      if (!setters) return;
      setters.setLeft(null);
      setters.setRight(null);
    };
    // setters identity is stable for the page's lifetime; cleanup runs
    // on unmount.
  }, []);
}
